import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { EvalBudgetStatus } from '@root/domain/schemas/evals/eval-budget.schema';
import type {
  EvalFindingContent,
  EvalResolutionCheckContent,
  EvalRunContent,
  EvalRunStepContent,
} from '@root/domain/schemas/evals/eval-run.schema';
import axios from 'axios';

/**
 * PBQA eval-run network layer — Contract Review Bot evaluation period.
 *
 * Kept deliberately narrow: we list runs for a submission and fetch the
 * full detail (run header + steps + findings) for the selected run. The
 * feature module polls these from the drawer. This file, together with
 * src/mutations/evalRuns.ts, can be removed when the eval period wraps
 * up.
 */

export type EvalRunSummary = Omit<EvalRunContent, 'triggered_by'> & {
  id: string;
  created_at: string;
  triggered_by?: { name?: string };
};

export type EvalRunStep = EvalRunStepContent & {
  id: string;
  created_at: string;
};

export type EvalFinding = EvalFindingContent & {
  id: string;
  created_at: string;
};

/**
 * One AI resolution check ("have my edits fixed these findings?") against a
 * completed run. `triggered_by` is narrowed the same way as on the run
 * summary — the drawer only ever needs the name.
 */
export type EvalResolutionCheck = Omit<EvalResolutionCheckContent, 'triggered_by'> & {
  id: string;
  created_at: string;
  triggered_by?: { name?: string };
};

export type EvalRunDetail = {
  run: EvalRunSummary;
  steps: EvalRunStep[];
  findings: EvalFinding[];
  /** Newest first (the handler sorts); `[]` when no check has ever run. */
  resolution_checks: EvalResolutionCheck[];
  /**
   * True when the contract has edits no scan or completed re-check has
   * assessed; false when it is unchanged since the last one; undefined when
   * the server could not say (run incomplete, no snapshot, check in flight).
   */
  has_unchecked_changes?: boolean;
};

export const fetchEvalRuns = async (submissionId: string): Promise<EvalRunSummary[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export type { EvalBudgetStatus };

/**
 * The caller's daily token budget (tenant-wide + their own cap). Rides on
 * the collection route with `?view=budget`; the drawer fetches it on open so
 * "Run evaluation" can be disabled instead of the click hitting a 429.
 */
export const fetchEvalBudget = async (submissionId: string): Promise<EvalBudgetStatus> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs?view=budget`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalRun = async (submissionId: string, runId: string): Promise<EvalRunDetail> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
  });
  const response = await axios(networkPayload);
  return response.data;
};
